import { useEffect, useState, useCallback, FormEvent } from 'react';
import { Card, Form, Spinner } from 'react-bootstrap';
import { OptionI, tooltipInterface } from '../../../types/interface';
import { useAppDispatch } from '../../redux/HooksRedux';
import * as tooltipAction from '../../redux/tooltip/tooltipAction';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertToLabelValue
} from 'utils/helpers';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { t } from 'i18next';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import StandardInput from 'base/StandardInput/StandardInput';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { StandardLoader } from 'base/Loader/Loader';
import { DataNotFound } from 'base/DataNotFound/DataNotFound';
import { ENGLISH, modules, SELECTED_LANGUAGE } from 'utils/enums/misc';
import { getCookie } from 'utils/cookieUtils';

export const Tooltip = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [formChange, setFormChange] = useState('application-health');
  const [formOption, setFormOption] = useState<OptionI[]>([]);
  const [tooltipData, setTooltipData] = useState<tooltipInterface[]>([]);
  const dispatch = useAppDispatch();
  const [storeOldTooltipValue, setStoreOldTooltipValue] = useState<
    tooltipInterface[]
  >([]);
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const currentLanguage = getCookie(SELECTED_LANGUAGE);
  console.log(isLoading);
  const getAllTooltipPages = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(tooltipAction.getAllTooltipPages());
      if (response.status === 200) {
        const convertValue = convertToLabelValue(
          response.data.pages,
          'form_name',
          'form_id'
        );
        setFormOption(convertValue);
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllTooltipInput = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        tooltipAction.getAllTooltipInput(formChange)
      );
      if (response.status === 200) {
        setTooltipData(response.data.data);
        setStoreOldTooltipValue(response.data.data);
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize the handleChange function to prevent it from being recreated on each render
  const handleChange = useCallback(
    (e: string, index: number, key: keyof tooltipInterface) => {
      setTooltipData(prevState => {
        return prevState.map((item, i) => {
          if (index === i) {
            return {
              ...item,
              [key]: e
            };
          } else {
            return item;
          }
        });
      });
    },
    []
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!checkAccess([modules.TOOLTIP.edit], userPermission)) {
      return errorNotification(t('tooltip.error.text_1'));
    }
    setIsLoading(true);
    const tooltips: tooltipInterface[] = [];
    tooltipData.map(item => {
      storeOldTooltipValue.map(oldTooltip => {
        if (oldTooltip.id === item.id) {
          if (oldTooltip.tooltip_en && oldTooltip.tooltip_de) {
            tooltips.push({
              tooltip_slug: item.id as string,
              tooltip_en: item.tooltip_en?.trimStart().trimEnd() as string,
              old_tooltip_en: oldTooltip.tooltip_en?.trimStart().trimEnd(),
              old_tooltip_de: oldTooltip.tooltip_de?.trimStart().trimEnd(),
              tooltip_de: item.tooltip_de?.trimStart().trimEnd() as string
            } as tooltipInterface);
          } else {
            tooltips.push({
              tooltip_slug: item.id as string,
              tooltip_en: item.tooltip_en?.trimStart().trimEnd() as string,
              tooltip_de: item.tooltip_de?.trimStart().trimEnd() as string
            });
          }
        }
      });
    });

    try {
      const response = await dispatch(
        tooltipAction.updateTooltipInput(formChange, {
          tooltips: tooltips
        })
      );
      if (response.status === 200) {
        successNotification(response.data?.message);
        getAllTooltipInput();
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // setIsLoading(true);
    getAllTooltipPages();
  }, []);

  useEffect(() => {
    if (!(formChange === '')) {
      // setIsLoading(true);
      getAllTooltipInput();
    }
  }, [formChange]);

  return (
    <>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <div className="d-flex justify-content-between mb-2 align-items-center flex-column flex-sm-row">
            <div className="w-auto">
              <h3 className="">
                {capitalizeFirstLetter(t('header.text.text_7'))}{' '}
              </h3>
            </div>
            <div className="d-flex gap-2 w-auto flex-column flex-sm-row">
              <StandardSelect
                value={formChange}
                onChange={e => setFormChange(e)}
                className={''}
                options={formOption}
                placeholder={t('tooltip.placeholder.text_1')}
                formClassName="w-330"
                id=""
                label={''}
                showError={false}
                validations={[]}
              />
              <ConditionalRender
                condition={checkAccess(
                  [
                    modules.TOOLTIP.create,
                    modules.TOOLTIP.edit,
                    modules.TOOLTIP.delete
                  ],
                  userPermission
                )}
              >
                <StandardButton
                  buttonText={t('general.common_text.text_18')}
                  onClick={() => console.log('save')}
                  type="submit"
                  className=""
                />
              </ConditionalRender>
            </div>
          </div>
          <Card>
            <Card.Body className="pt-0">
              <ConditionalRender
                condition={!isLoading && tooltipData?.length > 0}
                fallBack={
                  <DataNotFound
                    fullHeight
                    showAsCard={true}
                    description={[
                      t('general.no_data_des_1'),
                      t('general.no_data_des_2')
                    ]}
                  />
                }
              >
                <div className="">
                  <div className="row pb-2 mt-2 fs-9 border-bottom border-black mt-3 flex-nowrap">
                    <div className="col-1 col-lg-1 col-md-1  text-feather-soft-blue fw-bold fs-12 d-flex justify-content-start text-uppercase">
                      #
                    </div>
                    <div className="col-5 col-lg-2 col-md-2  fw-bold fs-12 text-feather-soft-blue text-uppercase">
                      {t('tooltip.general.text_1')}
                    </div>
                    <div className="col-7 col-lg-4 col-md-4 fw-bold fs-12 text-feather-soft-blue text-uppercase">
                      {t('tooltip.general.text_2')}
                    </div>
                    <div className="col-7 col-lg-4 col-md-4 fw-bold fs-12 text-feather-soft-blue text-uppercase">
                      {t('tooltip.general.text_3')}
                    </div>
                  </div>
                  {tooltipData.map((item, index: number) => {
                    return (
                      <div
                        className="row align-items-center py-1 border-bottom border-translucent flex-nowrap"
                        key={`tooltip-table-${index}`}
                      >
                        <div className="col-1 col-lg-1 col-md-1  text-moonless-mystery fs-16 font-bold">
                          {index + 1}
                        </div>
                        <div className="col-5 col-lg-2 col-md-2  text-moonless-mystery fs-16 font-bold">
                          {currentLanguage === ENGLISH
                            ? capitalizeFirstLetter(
                                item.placeholder?.replace(
                                  /[_-]/g,
                                  ' '
                                ) as string
                              )
                            : capitalizeFirstLetter(
                                item.placeholder_german as string
                              )}
                        </div>
                        <div className="col-7 col-lg-4 col-md-4 mt-1">
                          <StandardInput
                            id=""
                            value={`${item.tooltip_en}`}
                            onChange={e =>
                              handleChange(e as string, index, 'tooltip_en')
                            }
                            className={' text-moonless-mystery fs-16 font-bold'}
                            showError={false}
                            label=""
                            placeholder=""
                            type=""
                            validations={[]}
                          />
                        </div>
                        <div className="col-7 col-lg-4 col-md-4 mt-1">
                          <StandardInput
                            id="two"
                            value={`${item.tooltip_de}`}
                            onChange={e =>
                              handleChange(e as string, index, 'tooltip_de')
                            }
                            className={
                              'text-moonless-mystery fs-16 font-bold mb-0'
                            }
                            showError={false}
                            label=""
                            placeholder=""
                            type=""
                            validations={[]}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ConditionalRender>
            </Card.Body>
          </Card>
        </Form>
      </ConditionalRender>
    </>
  );
};
